import { useTheme } from "../../../context/ThemeProvider";
import hugerteLightSkinCss from "hugerte/skins/ui/oxide/skin.min.css?raw";
import hugerteDarkSkinCss from "hugerte/skins/ui/oxide-dark/skin.min.css?raw";
import { editorPages, editorImages } from "../../components/common/editorOptions";
import { ContentService } from "../../../services/ContentService";
import { MediaService } from "../../../services/MediaService";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type CSSProperties } from "react";
import { SiteSwitcher } from "../../../services/SiteSwitcher";
import Loading from "../../components/common/Loading";
import { iframeMessageHandlers } from "./Designer";
import { ContentEditorService } from "../../../services/ContentEditorService";
import { IContentComponent, IRefreshComponentPreviewHandle } from "../../../models/Interfaces";
import { withSandboxCookieShim } from "../../components/common/sandboxCookieShim";
import { inlineHtmlDocument } from "./inlineHtmlDocument";
import { inlineHtmlRegions, withInlineRegion } from "./inlineHtml";
import { useConfirm } from "../../../config/ConfirmProvider";
import { Alert, Button } from "react-bootstrap";
import "./inlineHtml.scss";
import FloatingInlineToolbar, { type InlineToolbarPlacement } from "./FloatingInlineToolbar";
import { INLINE_TOOLBAR_COMMAND, INLINE_TOOLBAR_STATE, type InlineToolbarState } from "./inlineToolbarProtocol";
import { componentDraftPreview } from "./componentDraftPreview";
import { compactEditorDialog } from "./compactEditorDialog";
import { editorPreviewDocument } from "./editorPreviewActions";

interface IPreviewFrameContext {
  pageCss?: string;
  pageClass?: string;
  sectionClass?: string;
  sectionId?: string;
  pageStyle?: CSSProperties;
  sectionStyle?: CSSProperties;
}

interface IComponentPreview extends IPreviewFrameContext {
  ApiTag: string;
  ContentComponentId: number;
  VersionId: number;
  ContentLoaded?: (apitag: string) => void;
  IsGrid: boolean;
  Component?: IContentComponent;
  DraftComponent?: IContentComponent;
  SaveInlineComponent?: (component: IContentComponent) => Promise<boolean>;
  EditComponent?: (component: IContentComponent, propertyName?: string) => void;
  PropertiesOpen?: boolean;
  OnInlineDirtyChange?: (id: number, dirty: boolean) => void;
}

const hasDeclaredStyle = (style?: CSSProperties): boolean => {
  if (!style) {
    return false;
  }

  return Object.values(style).some((value) => value !== undefined && value !== "");
};

const applyDeclaredStyle = (element: HTMLElement, style?: CSSProperties): void => {
  if (!style) {
    return;
  }

  const declared = style as Record<string, string | number>;
  const target = element.style as unknown as Record<string, string | number>;

  for (const [key, value] of Object.entries(declared)) {
    if (value === undefined || value === "") {
      continue;
    }

    target[key] = value;
  }
};

// The component document has the block and the site stylesheets. The public page
// puts PageClass and the section id/class around that block, which is what the
// theme selectors match. Those wrappers stay inside this frame.
export const wrapComponentPreview = (html: string, context: IPreviewFrameContext): string => {
  const pageClass = context.pageClass?.trim() ?? "";
  const sectionClass = context.sectionClass?.trim() ?? "";
  const sectionId = context.sectionId?.trim() ?? "";

  if (!html || (!context.pageCss?.trim() && !pageClass && !sectionClass && !sectionId && !hasDeclaredStyle(context.pageStyle) && !hasDeclaredStyle(context.sectionStyle))) {
    return html;
  }

  const document = new DOMParser().parseFromString(html, "text/html");
  const body = document.body;
  if (context.pageCss?.trim()) {
    const style = document.createElement("style");
    style.textContent = context.pageCss.replace(/<\/style/gi, "<\\/style");
    document.head.appendChild(style);
  }

  if (!body) {
    return html;
  }

  document.documentElement.classList.remove("hw100", "hideOverFlow");
  body.classList.remove("hw100");

  const page = document.createElement("div");
  page.className = pageClass;
  applyDeclaredStyle(page, context.pageStyle);

  const section = document.createElement("div");
  section.className = sectionClass;
  if (sectionId) {
    section.id = sectionId;
  }
  applyDeclaredStyle(section, context.sectionStyle);

  while (body.firstChild) {
    section.appendChild(body.firstChild);
  }

  page.appendChild(section);
  body.appendChild(page);

  return `<!DOCTYPE html>${document.documentElement.outerHTML}`;
};

const ComponentPreview = forwardRef<IRefreshComponentPreviewHandle, IComponentPreview>((props, ref) => {
  const { theme } = useTheme();
  const siteIdRef = useRef<string>();
  const [contentLoading, setContentLoading] = useState<boolean>(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const contentEditSvcRef = useRef<ContentEditorService | null>();

  const formatToolbarRef = useRef<HTMLDivElement>(null);
  const toolbarPlacementRef = useRef<InlineToolbarPlacement>({ expandedHeight: 0 });
  const [editingAnchor, setEditingAnchor] = useState<{ left: number; top: number; width: number; height: number }>();
  const [formatState, setFormatState] = useState<InlineToolbarState>({ ready: false });
  const actionsRef = useRef<HTMLDivElement>(null);
  const [inlineActive, setInlineActive] = useState(false);
  const [overlayBounds, setOverlayBounds] = useState<CSSProperties>({});
  const [overlayPlaceholderHeight, setOverlayPlaceholderHeight] = useState(0);
  const [inlineDialog, setInlineDialog] = useState(false);
  const scrollPositionRef = useRef<{ elements: { element: HTMLElement; top: number; left: number }[] }>();
  const scrollRestoreFrameRef = useRef(0);
  const [inlineOverlay, setInlineOverlay] = useState(false);
  const [frameHeight, setFrameHeight] = useState<number>();
  const [editing, setEditing] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const [interactionHtml, setInteractionHtml] = useState<string>();
  const [draft, setDraft] = useState("");
  const [initialDraft, setInitialDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const { requestConfirmation } = useConfirm();
  const [regionIndex, setRegionIndex] = useState(0);
  const [sourceHtml, setSourceHtml] = useState<string>("");
  const regions = useMemo(() => {
    const doc = new DOMParser().parseFromString(sourceHtml, "text/html");
    return inlineHtmlRegions(props.Component).filter(region => !region.selector || doc.querySelectorAll(region.selector)[region.index]);
  }, [props.Component, sourceHtml]);
  const region = regions[regionIndex] ?? regions[0];
  const property = region?.property;
  const canEdit = Boolean(region && props.SaveInlineComponent && !props.PropertiesOpen);
  const dirty = draft !== initialDraft;
  const regionDraftsRef = useRef<Record<string, { html: string; initial: string }>>({});
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const activateRegionRef = useRef(false);
  const initialDraftRef = useRef("");

  const [inlineGeneration, setInlineGeneration] = useState(0);
  const inlineToken = useMemo(() => crypto.randomUUID(), [inlineGeneration]);
  useEffect(() => { setFormatState({ ready: false }); setEditingAnchor(undefined); }, [inlineToken, regionIndex]);
  const toolbarRegionRef = useRef(regionIndex);
  if (toolbarRegionRef.current !== regionIndex) {
    toolbarPlacementRef.current = { expandedHeight: 0 };
    toolbarRegionRef.current = regionIndex;
  }
  const editingRef = useRef(false);
  const savingRef = useRef(false);
  savingRef.current = saving;
  const inlineTokenRef = useRef(inlineToken);
  inlineTokenRef.current = inlineToken;
  const regionKey = region ? `${region.property.Id}:${region.index}:${region.itemField ?? ""}` : "";
  const savedSource = interactionHtml ?? regionDraftsRef.current[regionKey]?.html ?? region?.html ?? "";
  const sendInline = (type: string, extra: object = {}) => {
    iframeRef.current?.contentWindow?.postMessage({ type, token: inlineTokenRef.current, ...extra }, "*");
  };

  useEffect(() => {
    props.OnInlineDirtyChange?.(props.ContentComponentId, (editing && dirty) || Object.keys(regionDraftsRef.current).length > 0);
    return () => props.OnInlineDirtyChange?.(props.ContentComponentId, false);
  }, [editing, dirty, regionIndex, inlineGeneration, props.OnInlineDirtyChange, props.ContentComponentId]);

  useEffect(() => {
    if (!props.PropertiesOpen) return;
    editingRef.current = false;
    setEditing(false);
    setInlineActive(false);
    setInlineOverlay(false);
    setInteracting(false);
  }, [props.PropertiesOpen]);

  const cancelEditing = async () => {
    if (saving) return;
    if (dirtyRef.current && !await requestConfirmation("Discard your unsaved HTML changes?", "Discard changes")) return;
    delete regionDraftsRef.current[regionKey];
    setInteractionHtml(undefined);
    editingRef.current = false;
    setEditing(false);
    setInteracting(false);
    setInlineActive(false);
    setInlineOverlay(false);
    setSaveError(false);
    setInlineGeneration(value => value + 1);
  };

  const selectRegion = async (index: number, activate = false) => {
    if (savingRef.current || index === regionIndex || !Number.isInteger(index) || !regions[index]) return;
    if (editingRef.current) {
      if (dirtyRef.current) regionDraftsRef.current[regionKey] = { html: draftRef.current, initial: initialDraftRef.current };
      else delete regionDraftsRef.current[regionKey];
    }
    editingRef.current = false;
    setEditing(false);
    setDraft("");
    setInitialDraft("");
    setInteracting(false);
    setInlineActive(false);
    setInlineOverlay(false);
    setSaveError(false);
    setInteractionHtml(undefined);
    activateRegionRef.current = activate;
    setRegionIndex(index);
    setInlineGeneration(value => value + 1);
  };

  const saveInline = async (html: string) => {
    if (!props.Component || !region || !props.SaveInlineComponent || saving) return;
    savingRef.current = true;
    setSaving(true);
    sendInline("inline-html-lock", { locked: true });
    setSaveError(false);
    try {
      const saved = await props.SaveInlineComponent(withInlineRegion(props.Component, region, html));
      if (saved) {
        setInteractionHtml(undefined);
        delete regionDraftsRef.current[regionKey];
        setInteracting(false);
        editingRef.current = false;
        setEditing(false);
        setInlineActive(false);
        setInlineOverlay(false);
        reportedLoad.current = false;
        setContentLoading(true);
        getPreview();
      } else setSaveError(true);
    } catch {
      setSaveError(true);
    } finally {
      savingRef.current = false;
      setSaving(false);
      sendInline("inline-html-lock", { locked: false });
    }
  };

  useEffect(() => {
    if ((!editing || !dirty) && Object.keys(regionDraftsRef.current).length === 0) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [editing, dirty, regionIndex]);

  const reportedLoad = useRef(false);
  const pageStyleKey = JSON.stringify(props.pageStyle ?? {});
  const sectionStyleKey = JSON.stringify(props.sectionStyle ?? {});
  const content = useMemo(() => {
    const document = canEdit && sourceHtml
      ? inlineHtmlDocument(sourceHtml, savedSource, inlineToken, window.location.origin, undefined, region ? { region, regions } : undefined)
      : componentDraftPreview(sourceHtml, props.Component, props.DraftComponent);
    return withSandboxCookieShim(editorPreviewDocument(wrapComponentPreview(document, {
      pageClass: props.pageClass, sectionClass: props.sectionClass, sectionId: props.sectionId,
      pageStyle: props.pageStyle, sectionStyle: props.sectionStyle, pageCss: props.pageCss
    })));
  }, [sourceHtml, props.DraftComponent, savedSource, canEdit, inlineToken, inlineGeneration, region, regions, regionIndex,
    props.pageClass, props.pageCss, props.sectionClass, props.sectionId, pageStyleKey, sectionStyleKey]);

  useEffect(() => {
    if (!canEdit) return;
    const mediaService = new MediaService();
    let active = true;
    let pageList: unknown = [];
    const updateImages = () => {
      const scope = mediaService.getMediaTreeState().scope;
      setMediaError(mediaService.getMediaTreeState().error);
      if (mediaService.getMediaTreeState().items === undefined) sendInline("inline-html-lists", { pages: pageList, images: [] });
      void editorImages(mediaService).then(images => {
        if (active && mediaService.getMediaTreeState().scope === scope) sendInline("inline-html-lists", { pages: pageList, images });
      }).catch(() => {});
    };
    const unsubscribeMedia = mediaService.subscribeMediaTree(updateImages);
    const receive = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow || event.data?.token !== inlineToken) return;
      if (event.data.type === "inline-html-editing-anchor") {
        const bounds = event.data.bounds;
        if (bounds && [bounds.left, bounds.top, bounds.width, bounds.height].every(value => typeof value === "number" && Number.isFinite(value) && Math.abs(value) < 100000))
          setEditingAnchor({ left: bounds.left, top: bounds.top, width: Math.max(0, bounds.width), height: Math.max(0, bounds.height) });
        return;
      }
      if (event.data.type === INLINE_TOOLBAR_STATE) {
        const state = event.data.state;
        if (!state || typeof state.ready !== "boolean") return;
        const text = (value: unknown) => typeof value === "string" ? value.slice(0, 80) : "";
        setFormatState({ ready: state.ready, bold: state.bold === true, italic: state.italic === true,
          underline: state.underline === true, strikethrough: state.strikethrough === true,
          unorderedList: state.unorderedList === true, orderedList: state.orderedList === true,
          canUndo: state.canUndo === true, canRedo: state.canRedo === true,
          format: text(state.format), fontName: text(state.fontName), fontSize: text(state.fontSize) });
        return;
      }
      if (event.data.type === "inline-html-refresh-media") {
        void mediaService.refreshMediaTree().catch(() => {});
        return;
      }
      if (event.data.type === "inline-html-cancel-request") { void cancelEditing(); return; }
      if (event.data.type === "inline-html-select-region") {
        void selectRegion(Number(event.data.index), true);
        return;
      }
      if (event.data.type === "inline-html-preserve-scroll") {
        // Focus in a sandboxed child can scroll its ancestor canvas even with
        // preventScroll. Restore the position recorded before the UI command.
        const position = scrollPositionRef.current;
        if (!position) return;
        const restore = () => position.elements.forEach(({ element, top, left }) => {
          element.scrollTop = top; element.scrollLeft = left;
        });
        cancelAnimationFrame(scrollRestoreFrameRef.current);
        restore();
        scrollRestoreFrameRef.current = requestAnimationFrame(() => {
          restore();
          scrollRestoreFrameRef.current = requestAnimationFrame(() => { restore(); scrollRestoreFrameRef.current = 0; });
        });
        return;
      }
      if (event.data.type === "inline-html-ready") {
        if (activateRegionRef.current) { activateRegionRef.current = false; sendInline("inline-html-activate"); }
        const scope = mediaService.getMediaTreeState().scope;
        void Promise.all([editorPages(new ContentService()), editorImages(mediaService).catch(() => null)]).then(([pages, images]) => {
          pageList = pages;
          if (active && mediaService.getMediaTreeState().scope === scope && event.source === iframeRef.current?.contentWindow) sendInline("inline-html-lists", { pages, images });
        }).catch(() => setSaveError(true));
        return;
      }
      if (event.data.type === "inline-html-blur") {
        if (!actionsRef.current?.contains(document.activeElement) && !formatToolbarRef.current?.contains(document.activeElement)) {
          setInlineActive(false);
          sendInline("inline-html-deactivate");
        }
        return;
      }
      if (event.data.type === "inline-html-overlay") {
        if (event.data.open) {
          let canvas = iframeRef.current?.parentElement ?? null;
          while (canvas && !/(auto|scroll)/.test(getComputedStyle(canvas).overflowY)) canvas = canvas.parentElement;
          const bounds = canvas?.getBoundingClientRect();
          const left = Math.max(16, (bounds?.left ?? 0) + 16);
          const top = Math.max(16, (bounds?.top ?? 0) + 16);
          const frame = iframeRef.current?.getBoundingClientRect();
          if (frame && iframeRef.current?.style.position !== "fixed") setOverlayPlaceholderHeight(frame.height);
          setOverlayBounds(event.data.dialog && frame
            ? compactEditorDialog(frame, { left: Math.max(0, bounds?.left ?? 0), top: Math.max(0, bounds?.top ?? 0), right: Math.min(innerWidth, bounds?.right ?? innerWidth), bottom: Math.min(innerHeight, bounds?.bottom ?? innerHeight) })
            : { left, top, width: Math.max(1, Math.min(bounds?.right ?? innerWidth, innerWidth) - left - 16), height: Math.max(1, Math.min(bounds?.bottom ?? innerHeight, innerHeight) - top - 16) });
        }
        setInlineOverlay(Boolean(event.data.open));
        setInlineDialog(Boolean(event.data.dialog));
        if (event.data.open) setInlineActive(true);
        return;
      }
      if (event.data.type === "inline-html-height") { applyFrameHeight(Number(event.data.height)); return; }
      if (typeof event.data.html !== "string") return;
      const { type, html } = event.data;
      if (type === "inline-html-interaction-snapshot") {
        // Freeze the selected view in a new frame, stopping site timers/scripts.
        // Keep the pre-interaction baseline for any existing unsaved edits.
        if (dirtyRef.current) regionDraftsRef.current[regionKey] = { html, initial: initialDraftRef.current };
        setInteractionHtml(html);
        setInteracting(false);
        editingRef.current = false;
        setEditing(false);
        setInlineActive(false);
        activateRegionRef.current = true;
        setInlineGeneration(value => value + 1);
        return;
      }
      if (!["inline-html-focus", "inline-html-change", "inline-html-save"].includes(type)) return;
      if (type === "inline-html-save") { void saveInline(html); return; }
      if (savingRef.current) return;
      if (!editingRef.current) {
        editingRef.current = true;
        initialDraftRef.current = regionDraftsRef.current[regionKey]?.initial ?? html;
        setInitialDraft(initialDraftRef.current);
        setSaveError(false);
      }
      dirtyRef.current = html !== initialDraftRef.current;
      draftRef.current = html;
      setDraft(html);
      setEditing(true);
      if (type === "inline-html-focus") setInlineActive(true);
    };
    window.addEventListener("message", receive);
    return () => { active = false; unsubscribeMedia(); window.removeEventListener("message", receive); };
  }, [canEdit, inlineToken, props.Component, props.SaveInlineComponent, region, regions, regionIndex]);

  useEffect(() => {
    // Two animation frames allow the expanded iframe to reach its final size.
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => sendInline("inline-html-overlay-ready"));
    });
    return () => { cancelAnimationFrame(firstFrame); cancelAnimationFrame(secondFrame); };
  }, [inlineOverlay]);

  useEffect(() => {
    if (!inlineActive || inlineOverlay || inlineDialog) return;
    const leaveComponent = (event: PointerEvent) => {
      const target = event.target as Node;
      if (target === iframeRef.current || actionsRef.current?.contains(target) || formatToolbarRef.current?.contains(target)) return;
      setInlineActive(false);
      sendInline("inline-html-deactivate");
    };
    document.addEventListener("pointerdown", leaveComponent, true);
    return () => document.removeEventListener("pointerdown", leaveComponent, true);
  }, [inlineActive, inlineOverlay, inlineDialog]);

  useEffect(() => {
    if (!inlineActive) return;
    let scheduled = 0;
    const positionToolbar = () => {
      scheduled = 0;
      const frame = iframeRef.current;
      if (!frame) return;
      if (!scrollRestoreFrameRef.current) {
        const elements: { element: HTMLElement; top: number; left: number }[] = [];
        for (let element = frame.parentElement; element; element = element.parentElement) {
          elements.push({ element, top: element.scrollTop, left: element.scrollLeft });
        }
        scrollPositionRef.current = { elements };
      }
      let scrollContainer = frame.parentElement;
      while (scrollContainer && !/(auto|scroll)/.test(getComputedStyle(scrollContainer).overflowY)) {
        scrollContainer = scrollContainer.parentElement;
      }
      // Leave room for the designer's floating page controls.
      const top = Math.max(0, scrollContainer?.getBoundingClientRect().top ?? 0) + 64;
      const offset = inlineOverlay ? 0 : Math.max(0, top - frame.getBoundingClientRect().top);
      const bottom = Math.min(innerHeight, scrollContainer?.getBoundingClientRect().bottom ?? innerHeight);
      sendInline("inline-html-toolbar-position", { offset, availableHeight: Math.max(100, bottom - Math.max(top, frame.getBoundingClientRect().top)) });
    };
    const schedule = () => {
      if (!scheduled) scheduled = requestAnimationFrame(positionToolbar);
    };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    positionToolbar();
    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(scheduled);
      cancelAnimationFrame(scrollRestoreFrameRef.current);
      scrollRestoreFrameRef.current = 0;
    };
  }, [inlineActive, inlineOverlay, frameHeight]);

  useImperativeHandle(ref, () => ({
    refreshPreview() {
      reportedLoad.current = false;
      setContentLoading(true);
      getPreview();
    }
  }));

  const applyFrameHeight = (height: number) => {
    if (!iframeRef.current || props.IsGrid || !Number.isFinite(height) || height <= 0) {
      return;
    }

    setFrameHeight(height + (canEdit ? 0 : 10));
  };

  const markLoaded = () => {
    if (reportedLoad.current) {
      return;
    }

    reportedLoad.current = true;
    setContentLoading(false);

    if (props.ContentLoaded) {
      props.ContentLoaded(props.ApiTag);
    }
  };

  const finalizePreviewLoad = (data?: { height?: number | string }) => {
    applyFrameHeight(Number(data?.height));
    markLoaded();
  };

  const setUpMessageHandler = () => {
    const handler = (data: { height?: number | string }) => {
      finalizePreviewLoad(data);
    };

    // Register the handler for this component
    iframeMessageHandlers.set(`C-${props.ApiTag}`, handler);

    // Cleanup on unmount
    return () => {
      iframeMessageHandlers.delete(`C-${props.ApiTag}`);
    };
  };

  const sendEditorTheme = () => sendInline("inline-html-theme", {
    dark: theme === "midnight",
    skinCss: theme === "midnight" ? hugerteDarkSkinCss : hugerteLightSkinCss
  });
  useEffect(() => { sendEditorTheme(); }, [theme]);

  const getPreview = () : void => {
    if (contentEditSvcRef.current) {
      contentEditSvcRef.current.GetComponentPreview(props.ApiTag, props.VersionId)
        .then(res => {
          if (res) {
            reportedLoad.current = false;
            setSourceHtml(res);
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
  };

  useEffect(() => {
    if (!siteIdRef.current) {
      const siteswitcher = new SiteSwitcher();
      siteIdRef.current = siteswitcher.getSelectedSiteKey() ?? "";
    }

    if (!contentEditSvcRef.current) {
      contentEditSvcRef.current = new ContentEditorService();
    }

    const cleanup = setUpMessageHandler();
    getPreview();

    return cleanup;
  }, []);

  return (
    <div className={`position-relative h-100${inlineActive ? " html-inline-editor" : ""}`}>
      {canEdit && regions.some(region => ["accordionitems", "tabitems", "carousel"].includes(region.property.Type.Name.toLowerCase())) &&
        <div className="d-flex flex-wrap gap-1 mb-1" role="toolbar" aria-label="Preview items">
          {regions.filter(region => region.itemField && region.itemField !== "Title").map(region => {
            const index = regions.indexOf(region);
            return <Button key={`${region.property.Id}-${region.index}`} size="sm" variant={index === regionIndex ? "secondary" : "outline-secondary"}
              disabled={saving} onClick={() => void selectRegion(index, true)}>
              {region.property.Type.Name.toLowerCase() === "carousel" ? "Slide" : "Item"} {region.index + 1}
            </Button>;
          })}
        </div>}
      {editing && (inlineActive || interacting) && <FloatingInlineToolbar
        key={`${inlineToken}:${regionIndex}`} anchor={iframeRef} toolbarRef={formatToolbarRef} placementRef={toolbarPlacementRef} activeAnchor={editingAnchor} state={formatState} disabled={saving}
        hidden={inlineDialog} plainText={interacting || region?.plainText}
        onCommand={(command, value) => sendInline(INLINE_TOOLBAR_COMMAND, { command, ...(value !== undefined ? { value } : {}) })}
        onRefreshMedia={() => { void new MediaService().refreshMediaTree().catch(() => {}); }}>
        <div ref={actionsRef} className="html-inline-editor-actions" role="toolbar" aria-label="HTML editing"
        onClick={event => event.stopPropagation()} onDoubleClick={event => event.stopPropagation()}>
        {props.Component && props.EditComponent && !dirty && Object.keys(regionDraftsRef.current).length === 0 && <Button size="sm" variant="outline-secondary"
          disabled={saving} onClick={() => props.EditComponent?.(props.Component!)}>Properties</Button>}
        {Object.keys(regionDraftsRef.current).length > 0 && <span>Other fields have unsaved changes</span>}
        <span>{interacting ? "Interacting with HTML" : dirty ? "Unsaved text changes" : region?.property.DisplayLabel || region?.property.PropertyName || "Editing rendered HTML"}</span>
        <Button size="sm" variant={interacting ? "primary" : "outline-secondary"} disabled={saving}
          onClick={() => {
            if (!interacting) {
              if (dirtyRef.current) regionDraftsRef.current[regionKey] = { html: draftRef.current, initial: initialDraftRef.current };
              else delete regionDraftsRef.current[regionKey];
              setInteracting(true);
              setInlineActive(false);
              sendInline("inline-html-interact", { enabled: true });
            } else {
              sendInline("inline-html-snapshot-interaction");
            }
          }}>
          {interacting ? "Back to editing" : "Interact"}
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={saving || interacting} onClick={() => sendInline("inline-html-undo")}>Undo</Button>
        <Button size="sm" variant="outline-secondary" disabled={saving || interacting} onClick={() => sendInline("inline-html-redo")}>Redo</Button>
        <Button size="sm" variant="success" onClick={() => sendInline("inline-html-read")} disabled={saving || !dirty || interacting}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={cancelEditing} disabled={saving}>Cancel</Button>
        {mediaError && <div className="small text-warning" role="status">{mediaError}</div>}
        {saveError && <Alert variant="danger" role="alert">Could not save. Your changes are still here; try again.</Alert>}
        </div>
      </FloatingInlineToolbar>}
      {inlineOverlay && <div aria-hidden="true" style={{ height: overlayPlaceholderHeight }} />}
      <iframe
        ref={iframeRef}
        id={`PREVIEW-${props.ContentComponentId}`}
        className={`comp-preview-iframe${inlineOverlay ? " comp-preview-iframe--overlay" : ""}`}
        title={`PREV-${props.ApiTag}`}
        srcDoc={content}
        // Site scripts can size the frame, and cannot read or restyle the admin page.
        sandbox="allow-scripts"
        onLoad={() => {
          if (content) {
            sendEditorTheme();
            markLoaded();
          }
        }}
        style={{ height: props.IsGrid ? "100%" : frameHeight, ...(inlineOverlay ? { position: "fixed", ...overlayBounds, zIndex: 2000, background: "white", border: "1px solid #ddd", borderRadius: 8 } as CSSProperties : {}), visibility: contentLoading ? 'hidden' : 'visible', pointerEvents: canEdit ? "auto" : "none",
          minHeight: inlineOverlay || props.IsGrid ? 0 : frameHeight,
          maxHeight: inlineOverlay ? "90vh" : undefined }}
      />
      {contentLoading && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center">
          <Loading Message="" SpinSize="3x" />
        </div>
      )}
    </div>
  );
});

export default ComponentPreview;
